import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TableCellsIcon, ChartBarIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import api from '../../../services/api';

/**
 * Las piezas comunes del panel de rendimiento: formato, paleta, la card (con su
 * vista de tabla y su CSV), la fila de cifras clave y el hook que pide cada
 * endpoint. Ver docs/panel-rendimiento-cartera-plan.md.
 */

// ------------------------------------------------------------------ formato
const nf = new Intl.NumberFormat('es-ES');
const nf2 = new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nf0 = new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 });
export const eur = v => (v == null ? '—' : `${nf2.format(v)} €`);
/** Euros sin céntimos, para las cifras clave: 12.345 € se lee mejor que 12.345,67 €. */
export const eur0 = v => (v == null ? '—' : `${nf0.format(v)} €`);
export const pct = v => (v == null ? '—' : `${nf2.format(v)} %`);
export const num = v => (v == null ? '—' : nf.format(v));
export const fecha = d =>
  d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
export const mesCorto = m => {
  const [y, mm] = String(m).split('-').map(Number);
  return new Date(y, mm - 1, 1).toLocaleDateString('es-ES', { month: 'short', year: '2-digit' });
};
/** Quita el «3 · » que fuerza el orden; aquí ya llega ordenado. */
export const sinOrden = s => String(s || '').replace(/^\d+\s·\s/, '');

// ------------------------------------------------------------------ paleta
// Paleta categórica validada (dataviz, modo claro), en orden fijo y nunca
// ciclada: una serie novena se pliega en «Otros», que es gris (acromático = «el
// resto»). Magenta, amarillo y aqua quedan por debajo de 3:1 sobre blanco: por
// eso toda gráfica lleva leyenda con nombre y la tabla a un clic.
export const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
export const OTROS = '#898781';
export const COLOR = { s1: SERIES[0], s2: SERIES[1], s3: SERIES[2] };
export const TINTA_TENUE = '#64748b';
export const REJILLA = '#e2e8f0';
export const SUPERFICIE = '#ffffff';

/** Rampa secuencial de un solo tono, de claro a oscuro, para dimensiones ordenadas (cosechas). */
export const rampa = n =>
  Array.from({ length: n }, (_, i) => {
    const t = n === 1 ? 1 : i / (n - 1);
    return `hsl(213, 66%, ${78 - t * 46}%)`;
  });

export const ejes = (yTitulo, extra = {}) => ({
  x: { grid: { display: false }, ticks: { color: TINTA_TENUE } },
  y: {
    beginAtZero: true,
    grid: { color: REJILLA },
    border: { display: false },
    ticks: { color: TINTA_TENUE },
    title: { display: !!yTitulo, text: yTitulo, color: TINTA_TENUE },
  },
  ...extra,
});

export const leyenda = {
  position: 'bottom',
  labels: { usePointStyle: true, pointStyle: 'circle', padding: 14, font: { size: 12 } },
};

export const barra = (label, data, color, extra = {}) => ({
  type: 'bar',
  label,
  data,
  backgroundColor: color,
  borderRadius: 4,
  borderSkipped: 'start',
  barPercentage: 0.8,
  categoryPercentage: 0.8,
  ...extra,
});

/** Un segmento de una pila: 2 px de superficie entre segmentos, para que no parezcan uno. */
export const segmento = (label, data, color, stack = 'pila') => ({
  ...barra(label, data, color),
  stack,
  borderColor: SUPERFICIE,
  borderWidth: { top: 2 },
  borderRadius: 0,
});

export const linea = (label, data, color, extra = {}) => ({
  type: 'line',
  label,
  data,
  borderColor: color,
  backgroundColor: color,
  borderWidth: 2,
  pointRadius: 4,
  pointHoverRadius: 6,
  pointBorderColor: SUPERFICIE,
  pointBorderWidth: 2,
  tension: 0.2,
  ...extra,
});

// ------------------------------------------------------------------ CSV
/**
 * CSV que abre bien Excel en español: separador «;», coma decimal y BOM para
 * los acentos. Los números van sin formato de miles para que Excel los lea como
 * números.
 */
const BOM = String.fromCharCode(0xfeff);

export function toCsv(filas, columnas) {
  const celda = v => {
    if (v == null) return '';
    if (typeof v === 'number') return String(v).replace('.', ',');
    const s = String(v);
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const cab = columnas.map(c => celda(c.label)).join(';');
  const cuerpo = filas.map(f => columnas.map(c => celda(c.csv ? c.csv(f[c.key], f) : f[c.key])).join(';'));
  return BOM + [cab, ...cuerpo].join('\n');
}

export function descargarCsv(nombre, filas, columnas) {
  const blob = new Blob([toCsv(filas, columnas)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${nombre}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ------------------------------------------------------------------ tabla
/** Tabla genérica: columnas = [{ key, label, fmt, link }]. */
export const Tabla = ({ filas, columnas, max = null }) => {
  const visibles = max ? filas.slice(0, max) : filas;
  return (
    <div className='overflow-x-auto max-h-96'>
      <table className='min-w-full text-sm'>
        <thead className='sticky top-0 bg-white'>
          <tr className='text-left text-xs text-slate-500 border-b border-slate-200'>
            {columnas.map(c => (
              <th key={c.key} className='py-1.5 pr-4 font-medium whitespace-nowrap'>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {visibles.map((f, i) => (
            <tr key={i} className='border-b border-slate-100 last:border-0'>
              {columnas.map(c => {
                const v = c.fmt ? c.fmt(f[c.key], f) : (f[c.key] ?? '—');
                return (
                  <td key={c.key} className='py-1.5 pr-4 whitespace-nowrap text-slate-700'>
                    {c.link ? (
                      <a href={c.link(f)} className='text-blue-600 hover:underline' target='_blank' rel='noreferrer'>
                        {v}
                      </a>
                    ) : (
                      v
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {max && filas.length > max && (
        <p className='text-xs text-slate-400 mt-2'>
          Se enseñan {num(max)} de {num(filas.length)}. El CSV lleva todas.
        </p>
      )}
    </div>
  );
};

// ------------------------------------------------------------------ card
const Estado = ({ children, tono = 'tenue' }) => (
  <p
    className={`text-sm py-10 text-center ${tono === 'error' ? 'text-red-600' : tono === 'aviso' ? 'text-amber-700' : 'text-slate-400'}`}
  >
    {children}
  </p>
);

/**
 * El marco de cada gráfica.
 *
 * - `titulo` es una PREGUNTA, y `lectura` una línea que dice cómo leer la
 *   respuesta. `nota` va debajo, en pequeño: de dónde sale y qué no es.
 * - Cargando, error, vacío y «pendiente» se distinguen de un cero (memoria
 *   `seccion-vacia-dice-lo-contrario`): un hueco nunca afirma «no hay».
 * - Vista de tabla y descarga en CSV siempre que haya filas.
 */
export const Card = ({
  titulo,
  lectura,
  nota,
  estado,
  vacio,
  vacioTexto = 'Sin datos para esta cartera en este periodo.',
  pendiente,
  filas,
  columnas,
  csv,
  children,
  pie,
  ancho = false,
  alto = 280,
  maxFilas = null,
  // Un control propio de la gráfica (un filtro), entre la cabecera y el
  // contenido. Se ve siempre, también vacía: si no, una combinación sin datos
  // escondería el filtro con el que salir de ella.
  encabezado = null,
}) => {
  const [verTabla, setVerTabla] = useState(false);
  const conGrafico = !!children && alto !== null;
  const conTabla = !!columnas && Array.isArray(filas) && filas.length > 0;
  return (
    <section
      className={`bg-white border border-slate-200 rounded-lg p-4 flex flex-col ${ancho ? 'xl:col-span-2' : ''}`}
      aria-label={titulo}
    >
      <header className='flex items-start justify-between gap-3 mb-3'>
        <div className='min-w-0'>
          <h3 className='font-semibold text-slate-900'>{titulo}</h3>
          {lectura && <p className='text-sm text-slate-600 mt-0.5'>{lectura}</p>}
        </div>
        {estado === 'ok' && !vacio && conTabla && (
          <div className='flex shrink-0 gap-1'>
            {conGrafico && (
              <button
                type='button'
                onClick={() => setVerTabla(v => !v)}
                className='inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 px-2 py-1 rounded-lg border border-slate-200'
                aria-pressed={verTabla}
              >
                {verTabla ? <ChartBarIcon className='w-4 h-4' /> : <TableCellsIcon className='w-4 h-4' />}
                {verTabla ? 'Gráfico' : 'Tabla'}
              </button>
            )}
            {csv && (
              <button
                type='button'
                onClick={() => descargarCsv(csv, filas, columnas)}
                className='inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 px-2 py-1 rounded-lg border border-slate-200'
                title='Descargar en CSV'
              >
                <ArrowDownTrayIcon className='w-4 h-4' />
                CSV
              </button>
            )}
          </div>
        )}
      </header>
      {estado === 'ok' && encabezado}
      <div className='flex-1'>
        {estado === 'loading' && <Estado>Cargando…</Estado>}
        {estado === 'error' && (
          <Estado tono='error'>No se pudo cargar esta gráfica. Las demás no dependen de ella.</Estado>
        )}
        {estado === 'ok' && pendiente && <Estado tono='aviso'>{pendiente}</Estado>}
        {estado === 'ok' && !pendiente && vacio && <Estado>{vacioTexto}</Estado>}
        {estado === 'ok' && !pendiente && !vacio && (
          <>
            {(verTabla || !conGrafico) && conTabla ? (
              <Tabla filas={filas} columnas={columnas} max={maxFilas} />
            ) : alto === null ? (
              children
            ) : (
              <div style={{ height: alto }}>{children}</div>
            )}
          </>
        )}
      </div>
      {nota && <p className='text-xs text-slate-400 mt-3'>{nota}</p>}
      {pie}
    </section>
  );
};

// ------------------------------------------------------------------ cifras clave
/**
 * Una cifra clave. `tono`: 'alerta' la pinta en ámbar (algo que debería ser
 * cero no lo es), 'ok' en verde. Con texto, nunca solo color.
 */
export const Cifra = ({ etiqueta, valor, pie, tono: tonoPedido = 'neutral', cargando = false }) => {
  // Mientras carga no se sabe si es cero: nada de verde ni ámbar todavía.
  const tono = cargando ? 'neutral' : tonoPedido;
  const color =
    tono === 'alerta'
      ? 'border-amber-300 bg-amber-50'
      : tono === 'ok'
        ? 'border-emerald-200 bg-emerald-50'
        : 'border-slate-200 bg-white';
  return (
    <div className={`border rounded-lg px-3 py-2 ${color}`}>
      <p className='text-xs text-slate-500'>{etiqueta}</p>
      <p className='text-xl font-semibold text-slate-900 leading-tight tabular-nums'>{cargando ? '…' : valor}</p>
      {pie && !cargando && <p className='text-xs text-slate-500 mt-0.5'>{pie}</p>}
    </div>
  );
};

export const FilaCifras = ({ children }) => (
  <div className='grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2'>{children}</div>
);

export const Aviso = ({ children }) => (
  <div className='border border-amber-200 bg-amber-50 text-amber-900 text-sm rounded-lg px-3 py-2'>{children}</div>
);

// ------------------------------------------------------------------ datos
/**
 * Pide un endpoint del panel con los parámetros dados. Una respuesta que llega
 * después de cambiar el cliente o el periodo se descarta: si no, una card podría
 * quedarse con las cifras del cliente anterior.
 */
export function useEndpoint(path, params, { enabled = true } = {}) {
  const [state, setState] = useState({ estado: 'loading', data: null, period: null });
  const key = JSON.stringify(params || {});
  const turno = useRef(0);

  const cargar = useCallback(() => {
    if (!enabled) return;
    const mio = ++turno.current;
    setState(s => ({ ...s, estado: 'loading' }));
    api
      .get(path, { params: JSON.parse(key) })
      .then(({ data }) => {
        if (mio === turno.current) setState({ estado: 'ok', data: data.data, period: data.period || null });
      })
      .catch(() => {
        if (mio === turno.current) setState({ estado: 'error', data: null, period: null });
      });
  }, [path, key, enabled]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  return { ...state, recargar: cargar };
}
